import { getLogger } from '@sas/nova-core/logging';
import { Dict } from '@sas/nova-core/services/types';
import { Localizer } from '../../../../../../../locale/useTextLocalizer';
import { SearchResultCollectionResponse } from '../../../../../../services/catalogService';

import {
  AssetDetailsRequest,
  HistoryFolderMembersRequest,
  SearchFavoritesRequest,
  SearchIndex,
  SearchRequest,
  UpdateHistoryFolderMembersRequest,
  SearchIndexProperty,
  SearchIndexChild,
  SavedSearchResponse,
  SearchIndexElement,
} from '../../../../grpc_web_catalog/catalog.pb';
import {
  GetAssetDetails,
  GetSearchFacets,
  GetSearchSuggestions,
  GetFavoriteResults,
  GetHistoryFolderMembers,
  GetSearchIndices,
  GetTypes,
  RemoveHistoryFolderMembers,
  Search,
  SearchFavorites,
  UpdateHistoryFolderMembers,
  GetFavoritesFolderMemberUris,
} from '../../../../internal/catalogService';
import { IsCatalogAdvancedLicensed } from '../../../../internal/feature/registeredFeatures';
import {
  GetPreviousSearches,
  SetPreviousSearches,
} from '../../../../internal/service/preferences/access/preferencesServiceAccessorREST';
import {
  CatalogEntityDetails,
  CatalogSearchIndex,
  CatalogSearchIndexChild,
  CatalogSearchIndexElement,
  CatalogSearchIndexProperty,
  convertStructToFieldValueMapping,
  FacetInfo,
  FacetType,
  PreviousSearch,
  RegisteredType,
  ResultsFavorite,
  SearchResult,
  ValueSuggestions,
} from '../model/types';
import {
  addSearchResultToFavoritesRequest,
  favoritesRequestToFolderMember,
} from '../util/FavoritesHelper';
import { getEntityNameParts } from '../util/NameUtils';
import {
  checkLibraryOnSearchResult,
  setupTypeInformation,
} from '../../../../../../DataSelection/util/types';

const logger = getLogger('DataSelection');

// 376
export const search = async (
  query: string,
  start: number,
  limit: number,
  type: string,
  indices: Array<string>,
  localize: Localizer,
  registeredTypes?: Record<string, RegisteredType>
): Promise<SearchResultCollectionResponse> => {
  const request: SearchRequest = {
    query,
    start,
    limit,
    type,
    indices,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const searchResponse = await Search(request);

  const fixedResults: SearchResult[] = [];
  for (const rez of searchResponse.results) {
    const attributeMap = convertStructToFieldValueMapping(rez.attributes);
    const extension: string = attributeMap?.extension;
    const nameParts = getEntityNameParts(rez.name, extension);
    // use rest syntax to remove unneeded attributes
    const { attributes, ...result } = {
      ...rez,
      attributeMap,
      typeInformation: await setupTypeInformation({
        localize,
        item: {
          resourceuri: rez.resourceuri,
          registeredtype: rez.registeredtype,
          displaytype: rez.displaytype,
          type: rez.type,
          attributeMap,
        },
        registeredTypes,
      }), // shenanigans to handle the extra ui-only properties
      filename: rez.name,
      extension: nameParts.extension,
      name: nameParts.name,
    };
    checkLibraryOnSearchResult(result);
    /* DSD-
    buffer.pageResults.push(result.id);
    buffer.resultsById[result.id] = result;
    */
    fixedResults.push(result); // DSD+
  }

  return {
    count: searchResponse.count,
    nextStart: searchResponse.nextstart,
    items: fixedResults,
  };
};

// 1112
export const getSearchPropsCatalogEntityDetails = async (
  uri: string,
  localize: Localizer
): Promise<CatalogEntityDetails> => {
  const request: AssetDetailsRequest = {
    selfUri: uri,
    // skip auth check for performance reasons
    skipAuthCheck: true,
  };

  const [tempRez, err] = await GetAssetDetails(request);

  if (err || !tempRez) {
    throw err;
  }

  const attributeMap = convertStructToFieldValueMapping(tempRez.attributes);
  const catalogEntityDetails = {
    ...tempRez,
    attributeMap,
    typeInformation: await setupTypeInformation({
      localize,
      item: {
        resourceuri: tempRez.entity?.resourceid,
        attributeMap,
        type: tempRez.entity?.type || '',
      },
    }),
  };

  return catalogEntityDetails;
};

// 1608
export const getRegisteredTypes = async (): Promise<Record<string, RegisteredType>> => {
  const registeredTypes: Record<string, RegisteredType> = {};

  const typesResponse = await GetTypes();

  for (const regType of typesResponse.types) {
    const r: RegisteredType = { ...regType };
    registeredTypes[regType.name.toLocaleLowerCase()] = r;
  }

  return registeredTypes;
};

const convertProperties = (
  properties?: Dict<SearchIndexProperty>
): Array<CatalogSearchIndexProperty> => {
  if (properties === undefined) return [];
  return Object.keys(properties).map((k) => {
    const prop = properties[k];
    const newProp: CatalogSearchIndexProperty = {
      freetext: !!prop?.freeText,
      representation: prop?.representation || [],
      stagename: prop?.stageName,
    };
    return newProp;
  });
};

const convertChildren = (children?: Dict<SearchIndexChild>): Array<CatalogSearchIndexChild> => {
  const convertedChildren: Array<CatalogSearchIndexChild> = [];
  if (children) {
    for (const childName of Object.keys(children)) {
      const child = children?.[childName];
      if (child) {
        convertedChildren.push({
          name: childName,
          inverse: child.inverse,
          properties: convertProperties(child.properties),
          peer: convertSearchIndexElement(child.peer),
          stagename: child.stageName,
        });
      }
    }
  }
  return convertedChildren;
};

const convertSearchIndexElement = (searchIndex: SearchIndexElement): CatalogSearchIndexElement => {
  const r: CatalogSearchIndexElement = {
    ...searchIndex,
    stagename: searchIndex.stageName,
    typedefinition: searchIndex.typeDefinition,
    properties: convertProperties(searchIndex.properties),
    children: convertChildren(searchIndex.children),
  };
  return r;
};

const convertSearchIndex = (searchIndex: SearchIndex): CatalogSearchIndex => {
  const r: CatalogSearchIndex = {
    ...searchIndex,
    stagename: searchIndex.stageName,
    typedefinition: searchIndex.typeDefinition,
    properties: convertProperties(searchIndex.properties),
    children: convertChildren(searchIndex.children),
  };
  return r;
};

// 676
export const getCatalogSearchIndices = async (): Promise<Record<string, CatalogSearchIndex>> => {
  const searchIndices: Record<string, CatalogSearchIndex> = {};

  const searchIndicesResponse = await GetSearchIndices();

  for (const searchIndex of searchIndicesResponse.indices) {
    searchIndices[searchIndex.name.toLocaleLowerCase()] = convertSearchIndex(searchIndex);
  }

  return searchIndices;
};

// 2228
export const getFavoriteResults = async ({
  localize,
  registeredTypes,
  indices,
}: {
  localize: Localizer;
  registeredTypes?: Record<string, RegisteredType>;
  indices: Array<string>;
}): Promise<SearchResultCollectionResponse> => {
  const favoriteResults = await GetFavoriteResults(indices);

  const fixedResults: SearchResult[] = [];
  for (const rez of favoriteResults.results) {
    const attributeMap = convertStructToFieldValueMapping(rez.attributes);
    const extension = attributeMap?.extension;
    const nameParts = getEntityNameParts(rez.name, extension);
    // use rest syntax to remove unneeded attributes
    const { attributes, ...searchResult } = {
      ...rez,
      attributeMap,
      typeInformation: await setupTypeInformation({
        localize,
        item: {
          resourceuri: rez.resourceuri,
          registeredtype: rez.registeredtype,
          displaytype: rez.displaytype,
          type: rez.type,
          attributeMap,
        },
        registeredTypes,
      }),
      filename: rez.name,
      extension: nameParts.extension,
      name: nameParts.name,
    };
    /* DSD-
    buffer.pageFavorites.push(searchResult.resourceuri);
    buffer.favoritesByResourceUri[searchResult.resourceuri] = searchResult;
    */
    fixedResults.push(searchResult); // DSD+
  }

  return {
    count: fixedResults.length,
    items: fixedResults,
  };
};

export const getFavoritesFolderMemberUris = async (): Promise<Array<string>> => {
  const favoriteResults = await GetFavoritesFolderMemberUris();
  return favoriteResults;
};

export const getHistoryFolderMemberUris = async (): Promise<Array<string>> => {
  const request: HistoryFolderMembersRequest = {};

  const historyFolderMembers = await GetHistoryFolderMembers(request);

  const resourceUris: string[] = [];
  for (const member of historyFolderMembers.items) {
    resourceUris.push(member.uri);
  }
  return resourceUris;
};

export const getSearchResultsForResourceUris = async ({
  localize,
  resourceUris,
  registeredTypes,
  indices,
  start,
  limit,
}: {
  localize?: Localizer;
  resourceUris?: Array<string>;
  registeredTypes?: Record<string, RegisteredType>;
  indices?: Array<string>;
  start: number;
  limit: number;
}): Promise<{ items: Record<string, SearchResult>; uris: Array<string> } | null> => {
  if (
    resourceUris === undefined ||
    localize === undefined ||
    indices === undefined ||
    registeredTypes === undefined
  ) {
    return Promise.resolve(null);
  }
  const request: SearchFavoritesRequest = {
    resourceuris: resourceUris,
    indices,
    start,
    limit,
  };
  const result = await SearchFavorites(request);

  const foundUris: Array<string> = [];
  const itemsByUri: Record<string, SearchResult> = {};
  for (const rez of result.results) {
    const attributeMap = convertStructToFieldValueMapping(rez.attributes);
    const extension: string = attributeMap?.extension;
    const nameParts = getEntityNameParts(rez.name, extension);
    // use rest syntax to remove unneeded attributes
    const { attributes, ...searchResult } = {
      ...rez,
      attributeMap,
      typeInformation: await setupTypeInformation({
        localize,
        item: {
          resourceuri: rez.resourceuri,
          registeredtype: rez.registeredtype,
          displaytype: rez.displaytype,
          type: rez.type,
          attributeMap,
        },
        registeredTypes,
      }),
      filename: rez.name,
      extension: nameParts.extension,
      name: nameParts.name,
    };
    checkLibraryOnSearchResult(searchResult);
    foundUris.push(searchResult.resourceuri);
    /* DSD-
    foundRecentUrisMap[searchResult.resourceuri] = searchResult.id;
    */
    itemsByUri[searchResult.resourceuri] = searchResult;
  }

  return { items: itemsByUri, uris: foundUris };
};

// 2321
export const doUpdateRecentItems = async (item: SearchResult): Promise<void> => {
  return updateRecentItems(addSearchResultToFavoritesRequest([item], {})[0]);
};

// 2329
export const updateRecentItems = async (recentItem: ResultsFavorite): Promise<void> => {
  const request: HistoryFolderMembersRequest = {
    name: recentItem.name,
  };

  try {
    const historyFolderMembers = await GetHistoryFolderMembers(request);

    const match: ResultsFavorite | undefined = historyFolderMembers.items.find(
      (folderMember: ResultsFavorite) => folderMember.uri === recentItem.uri
    );

    if (match) {
      await replaceRecentItem(match, match.parentFolderUri);
    } else {
      await addRecentItem(recentItem);
    }
  } catch (error) {
    logger.warn(`Could not add ${recentItem.name} to recent items list.`);
  }

  return Promise.resolve();
};

// 2132
export const addRecentItem = async (recentItem: ResultsFavorite) => {
  const request: UpdateHistoryFolderMembersRequest = {
    item: favoritesRequestToFolderMember([recentItem])[0],
  };

  await UpdateHistoryFolderMembers(request);
};

// 2406
export const replaceRecentItem = async (
  recentItem: ResultsFavorite,
  parentUri?: string
): Promise<void> => {
  const request: UpdateHistoryFolderMembersRequest = {
    item: favoritesRequestToFolderMember([recentItem])[0],
    parentFolderUri: parentUri,
  };

  await RemoveHistoryFolderMembers(request);
  await addRecentItem(recentItem);

  return Promise.resolve();
};

export const getSearchSuggestions = async (searchProps: {
  indices?: Array<string>;
  facetInfo?: Pick<FacetInfo, 'name' | 'valueType'>;
  initialValue?: string;
  limit: number;
}): Promise<ValueSuggestions | undefined> => {
  if (
    searchProps.facetInfo !== undefined &&
    (searchProps.facetInfo.valueType === 'text' || searchProps.facetInfo.valueType === 'keyword') &&
    searchProps.indices !== undefined &&
    searchProps.initialValue !== undefined
  ) {
    const result = await GetSearchSuggestions({
      limit: searchProps.limit,
      initialValue: searchProps.initialValue,
      indices: searchProps.indices,
      fieldName: searchProps.facetInfo?.name,
    });
    return {
      value: searchProps.initialValue,
      suggestions: result,
      indices: searchProps.indices,
      limit: searchProps.limit,
    };
  } else {
    return Promise.resolve(undefined);
  }
};

export const getPreviousSearches = async (): Promise<Array<PreviousSearch> | null> => {
  const result = await GetPreviousSearches();
  const previousSearches: Array<PreviousSearch> = [];
  if (result !== null) {
    for (const ps of result) {
      previousSearches.push({
        query: ps.query,
        count: ps.count,
        indicesList: ps.indices,
        timestamp: ps.timestamp,
      });
    }
    return previousSearches;
  }

  return null;
};

const MAX_PREVIOUS_SEARCHES = 10;
export const addPreviousSearch = async (
  previousSearch: PreviousSearch
): Promise<Array<PreviousSearch> | null> => {
  const previousSearches = await GetPreviousSearches();
  const convertedPreviousSearch: SavedSearchResponse = {
    query: previousSearch.query,
    count: previousSearch.count,
    timestamp: previousSearch.timestamp,
    indices: previousSearch.indicesList,
  };
  const matchIndex = previousSearches
    ? previousSearches.findIndex(
        (ps) =>
          ps.query &&
          previousSearch.query &&
          ps.query.toLocaleLowerCase().trim() === previousSearch.query.toLocaleLowerCase().trim()
      )
    : -1;
  if (previousSearches && matchIndex > -1) {
    previousSearches.splice(matchIndex, 1);
  }
  const newPreviousSearches: Array<SavedSearchResponse> = previousSearches
    ? [convertedPreviousSearch, ...previousSearches]
    : [convertedPreviousSearch];
  if (newPreviousSearches.length > MAX_PREVIOUS_SEARCHES) {
    newPreviousSearches.splice(
      MAX_PREVIOUS_SEARCHES,
      newPreviousSearches.length - MAX_PREVIOUS_SEARCHES
    );
  }
  const result = await SetPreviousSearches(newPreviousSearches);
  const updatedPreviousSearches: Array<PreviousSearch> = [];
  if (result !== null) {
    for (const ps of result) {
      updatedPreviousSearches.push({
        query: ps.query,
        count: ps.count,
        indicesList: ps.indices,
        timestamp: ps.timestamp,
      });
    }
    return updatedPreviousSearches;
  }

  return null;
};

export const getSearchFacets = async (): Promise<Record<string, FacetInfo>> => {
  const response = await GetSearchFacets();
  const isCatalogAdvancedLicensed = await IsCatalogAdvancedLicensed();
  const facets: Record<string, FacetInfo> = {};
  const collectionLength = response.facets.length;
  if (collectionLength > 0) {
    for (const facetRepr of response.facets) {
      if (facetRepr && (isCatalogAdvancedLicensed || !facetRequiresLicense(facetRepr.name))) {
        // const facetLabel: string = localize('search.filterName.' + facetRepr.name + '.title');
        const facet: FacetInfo = {
          name: facetRepr.name,
          valueType: facetRepr.type ? (facetRepr.type as FacetType) : 'text',
          indices: facetRepr.indices,
          hiddenIndices: facetRepr.hiddenIndices,
          // label: facetLabel.startsWith('NovaComponents') ? facetRepr.name : facetLabel,
        };
        facets[facet.name.toLocaleLowerCase()] = facet;
      }
    }
  }
  return facets;
};

// facetRequiresLicense - Determine if the parameter string matches a list of facets
// that required a license to be displayed to the user
const facetRequiresLicense = (facetName: string): boolean => {
  switch (facetName) {
    case 'Classification':
    case 'Column.classification':
    // _C_lassificaiton is a bug in the current facet naming. Can remove the
    // following entry when that name is fixed and we can just use the lowercase entry.
    case 'Column.Classification':
    case 'Column.informationPrivacy':
    case 'Column.semanticType':
    case 'Contacts':
    case 'InformationPrivacy':
    case 'Locale':
    case 'Status':
    case 'Keywords':
    case 'Column.keywords':
    case 'Languages':
    case 'Tags':
    case 'Topic':
    case 'TopicKeywords':
    case 'TopicParent':
      return true;
    default:
      return false;
  }
};
